import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { X } from "lucide-react";
import { db } from "../lib/firebase";
import { useFirestoreDocOnce } from "../hooks/useFirestoreDocOnce";
import { useEventParticipants } from "../hooks/useEventParticipants";
import { useAllPersons } from "../hooks/useAllPersons";
import { useSnackbar } from "../context/SnackbarContext";
import type { Person, Session } from "@event-planner/shared";

interface SpeakerLinkerProps {
  eventId: string;
  sessionId: string;
}

// ADR-136: speakers are personIds on session.speakerIds[]. The picker
// surfaces two pools: claimed participants of this event (status=='active')
// and pending persons (authUid==null) anywhere in the system. The latter
// preserves the legacy cross-event picker UX so organizers can wire a
// speaker who isn't yet on the event roster.
type SpeakerOption = {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  photoURL: string | null;
  publicTitle: string | null;
  publicOrganization: string | null;
  pending: boolean;
};

function nullToEmpty(value: string | null | undefined): string {
  return value ?? "";
}

function toOption(person: Person, pending: boolean): SpeakerOption {
  return {
    id: person.id,
    displayName: person.displayName || person.email,
    firstName: nullToEmpty(person.firstName),
    lastName: nullToEmpty(person.lastName),
    photoURL: person.photoURL,
    publicTitle: person.publicTitle,
    publicOrganization: person.publicOrganization,
    pending,
  };
}

export function SpeakerLinker({ eventId, sessionId }: SpeakerLinkerProps) {
  const { showError } = useSnackbar();
  const [selectedSpeakers, setSelectedSpeakers] = useState<SpeakerOption[]>([]);
  const [busy, setBusy] = useState(false);

  const sessionRef = useMemo(() => doc(db, "sessions", sessionId), [sessionId]);
  const { data: session, loading: loadingSession, refetch: refetchSession } =
    useFirestoreDocOnce<Session>(sessionRef);

  const { persons: eventPersons, loading: loadingMembers } =
    useEventParticipants(eventId, ["active"]);
  const { data: allPersons, loading: loadingAll } = useAllPersons();

  const speakerIds: string[] = useMemo(
    () => session?.speakerIds ?? [],
    [session?.speakerIds],
  );

  const allOptions: SpeakerOption[] = useMemo(() => {
    const eventMemberIds = new Set(eventPersons.map((p) => p.id));
    const memberOptions = eventPersons.map((p) => toOption(p, false));
    const pendingOptions = allPersons
      .filter((p) => p.authUid === null && !eventMemberIds.has(p.id))
      .map((p) => toOption(p, true));
    return [...memberOptions, ...pendingOptions];
  }, [eventPersons, allPersons]);

  const optionsById = useMemo(() => {
    const m = new Map<string, SpeakerOption>();
    for (const o of allOptions) m.set(o.id, o);
    return m;
  }, [allOptions]);

  const availableOptions = useMemo(() => {
    const linked = new Set(speakerIds);
    return allOptions.filter((o) => !linked.has(o.id));
  }, [allOptions, speakerIds]);

  const linkedSpeakers = useMemo(() => {
    return speakerIds
      .map((personId) => optionsById.get(personId))
      .filter((o): o is SpeakerOption => o !== undefined);
  }, [speakerIds, optionsById]);

  const handleAddSelected = useCallback(async () => {
    if (selectedSpeakers.length === 0) return;
    setBusy(true);
    try {
      await updateDoc(sessionRef, {
        speakerIds: arrayUnion(...selectedSpeakers.map((s) => s.id)),
        updatedAt: serverTimestamp(),
      });
      setSelectedSpeakers([]);
      refetchSession();
    } catch (err) {
      console.error("Failed to add speaker:", err);
      showError(err instanceof Error ? err.message : "Failed to add speaker");
    } finally {
      setBusy(false);
    }
  }, [selectedSpeakers, sessionRef, refetchSession, showError]);

  const handleRemove = useCallback(
    async (personId: string) => {
      setBusy(true);
      try {
        await updateDoc(sessionRef, {
          speakerIds: arrayRemove(personId),
          updatedAt: serverTimestamp(),
        });
        refetchSession();
      } catch {
        showError("Failed to remove speaker");
      } finally {
        setBusy(false);
      }
    },
    [sessionRef, refetchSession, showError],
  );

  if (loadingSession || loadingMembers || loadingAll) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading speakers...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        multiple
        options={availableOptions}
        loading={loadingMembers}
        value={selectedSpeakers}
        onChange={(_, value) => setSelectedSpeakers(value)}
        getOptionLabel={(o) => o.displayName}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        groupBy={(o) => (o.pending ? "Pending people" : "Event members")}
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.toLowerCase();
          if (!q) return options;
          return options.filter(
            (o) =>
              o.displayName.toLowerCase().includes(q) ||
              o.firstName.toLowerCase().includes(q) ||
              o.lastName.toLowerCase().includes(q),
          );
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                <Avatar
                  src={option.photoURL ?? undefined}
                  sx={{ width: 28, height: 28 }}
                >
                  {option.firstName?.[0] || "?"}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{option.displayName}</Typography>
                  {(option.publicTitle || option.publicOrganization) && (
                    <Typography variant="caption" color="text.secondary">
                      {[option.publicTitle, option.publicOrganization]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  )}
                </Box>
                {option.pending && (
                  <Chip label="Pending" size="small" variant="outlined" />
                )}
              </Stack>
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...rest } = getTagProps({ index });
            return (
              <Chip
                key={key}
                avatar={
                  <Avatar src={option.photoURL ?? undefined}>
                    {option.firstName?.[0] || "?"}
                  </Avatar>
                }
                label={option.displayName}
                size="small"
                {...rest}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search people"
            placeholder="Type a name..."
            sx={{ mt: 1 }}
          />
        )}
        noOptionsText={
          allOptions.length === 0
            ? "No people available"
            : "No matching people"
        }
        disabled={busy}
      />

      {selectedSpeakers.length > 0 && (
        <Button
          variant="contained"
          size="small"
          onClick={handleAddSelected}
          disabled={busy}
          sx={{ mt: 1 }}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {busy
            ? "Adding..."
            : `Add ${selectedSpeakers.length} speaker${selectedSpeakers.length > 1 ? "s" : ""}`}
        </Button>
      )}

      {linkedSpeakers.length > 0 && (
        <List dense disablePadding sx={{ mt: 2 }}>
          {linkedSpeakers.map((speaker) => (
            <ListItem
              key={speaker.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleRemove(speaker.id)}
                  disabled={busy}
                  aria-label={`Remove ${speaker.displayName}`}
                >
                  <X size={16} />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar src={speaker.photoURL ?? undefined} alt={speaker.displayName}>
                  {speaker.firstName?.[0] ?? "?"}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Link
                      to={`/users/${speaker.id}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                      >
                        {speaker.displayName}
                      </Typography>
                    </Link>
                    {speaker.pending && (
                      <Chip label="Pending" size="small" variant="outlined" />
                    )}
                  </Stack>
                }
                secondary={
                  [speaker.publicTitle, speaker.publicOrganization]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {linkedSpeakers.length === 0 && selectedSpeakers.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No speakers linked to this session yet.
        </Typography>
      )}
    </Box>
  );
}
